import { Button, Chip, Div, Input, Label, Touchable, useWindowSize } from "@grassrootly/rn-core";
import { Image } from "react-native";
import { images } from "../../../layout/assets";
import { EditGameStatsProps, useEditGameStats } from "./useEditGameStats";
import { GameType } from "../../../screens/new-game/NewGame.types";
import { Fragment, useEffect } from "react";

export default function EditGameStats(props: EditGameStatsProps) {
  const { isMobile } = useWindowSize();
  const { updateDisabled, disableScoreEdit, game } = props;
  // Use disableScoreEdit if provided, otherwise fall back to updateDisabled
  const scoreEditDisabled = disableScoreEdit !== undefined ? disableScoreEdit : updateDisabled;
  const {
    updatePlayerStats,
    unsavedChanges,
    clearChanges,
    saveChanges,
    teamsStats,
    selectTeam,
    getSelectedTeam,
    setTeamGoals,
  } = useEditGameStats(props);

  return (
    <Div classNames={["flex1", "bgNeutralDarkerDark"]}>
      {/* Teams stats (goals and assists) / team selection */}
      <Div classNames={["mt-{5}"]}>
        <Div classNames={["flexRow", "bgNeutralDarkerDark", "justifyContentCenter"]}>
          {teamsStats.map((x, i) => (
            <Fragment key={x.teamKey}>
              {!scoreEditDisabled && i === 1 && (
                <Label classNames={["h4", "ml-{5}"]}>
                  <Input
                    value={x.goals.toString()}
                    keyboardType={"numeric"}
                    classNames={["w-{60}", "textCenter", "bgNeutralDarkerContrast", "pb-{2}", "rounded"]}
                    onChangeText={(t) => setTeamGoals(Number(t), x.teamKey)}
                  ></Input>
                </Label>
              )}
              {game.game_type === GameType.TRAINING && (
                <Touchable
                  classNames={[
                    x.selected ? "bgSuccessMain" : "bgSuccessWashed",
                    "p8",
                    "rounded",
                    "alignItemsCenter",
                    "gap4",
                    "flex1",
                    "justifyContentCenter",
                    "mh-{20}",
                  ]}
                  onPress={() => selectTeam(x.teamKey)}
                >
                  <Label
                    classNames={["colorSecondaryContrast"]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                    type={!isMobile ? "l2Regular" : "l3Regular"}
                  >
                    {x.teamLabel}
                  </Label>
                </Touchable>
              )}
              {!scoreEditDisabled && i === 0 && (
                <>
                  <Label classNames={["h4", "mr-{5}"]}>
                    <Input
                      value={x.goals.toString()}
                      keyboardType={"numeric"}
                      classNames={["w-{60}", "textCenter", "bgNeutralDarkerContrast", "pb-{2}", "rounded"]}
                      onChangeText={(t) => setTeamGoals(Number(t), x.teamKey)}
                    ></Input>
                  </Label>
                  <Label classNames={["h4"]}> - </Label>
                </>
              )}
            </Fragment>
          ))}
        </Div>
      </Div>

      {/* Team lineup list */}
      <Div classNames={["flex1", "pt-{6}", "mt-{4}", "bgNeutralDarkerDark"]}>
        {getSelectedTeam().map((team, indexMain) =>
          team.lineup.map((attendee, index) => (
            <Div
              key={index}
              classNames={[
                "p16",
                (indexMain + index) % 2 == 0 ? "bgNeutralDarkerMain" : "bgSecondaryMain",
                "flexRow",
                "alignItemsCenter",
              ]}
            >
              <Div classNames={isMobile ? ["flexColumn", "w100"] : ["flexRow", "w100"]}>
                <Div classNames={["flexRow", "alignItemsCenter", !isMobile ? "w50" : "w100"]}>
                  <Image
                    style={{ height: 40, width: 40, borderRadius: 40 }}
                    resizeMode="cover"
                    source={
                      attendee.player?.profile_picture ? { uri: attendee.player?.profile_picture } : images.playerAvatar
                    }
                  />
                  <Label classNames={["pl-{5}"]}>{attendee.player?.name}</Label>
                </Div>
                {!updateDisabled && (
                  <Div
                    classNames={[
                      "flexColumn",
                      !isMobile ? "w50" : "w100",
                      isMobile ? "mt-{8}" : "",
                      "gap4",
                    ]}
                  >
                    {/* Goals - Always show */}
                    <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                      <Label classNames={["l4Regular", "colorPrimaryMain"]}>Goals</Label>
                      <Div classNames={["flexRow", "alignItemsCenter"]}>
                        <Chip
                          title={"-"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, -1)}
                        ></Chip>
                        <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.goals_scored || 0}</Label>
                        <Chip
                          title={"+"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, 1)}
                        ></Chip>
                      </Div>
                    </Div>

                    {/* Assists - Always show */}
                    <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                      <Label classNames={["l4Regular", "colorPrimaryMain"]}>Assists</Label>
                      <Div classNames={["flexRow", "alignItemsCenter"]}>
                        <Chip
                          title={"-"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, -1)}
                        ></Chip>
                        <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.assists_made || 0}</Label>
                        <Chip
                          title={"+"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, 1)}
                        ></Chip>
                      </Div>
                    </Div>

                    {/* Own Goals - Always show */}
                    <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                      <Label classNames={["l4Regular", "colorPrimaryMain"]}>Own Goals</Label>
                      <Div classNames={["flexRow", "alignItemsCenter"]}>
                        <Chip
                          title={"-"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, undefined, -1)}
                        ></Chip>
                        <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.own_goals || 0}</Label>
                        <Chip
                          title={"+"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, undefined, 1)}
                        ></Chip>
                      </Div>
                    </Div>

                    {/* Big errors - Always show */}
                    <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                      <Label classNames={["l4Regular", "colorPrimaryMain"]}>Big errors</Label>
                      <Div classNames={["flexRow", "alignItemsCenter"]}>
                        <Chip
                          title={"-"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, -1)}
                        ></Chip>
                        <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.errors_leading_to_a_goal || 0}</Label>
                        <Chip
                          title={"+"}
                          classNames={["l2Regular"]}
                          onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, 1)}
                        ></Chip>
                      </Div>
                    </Div>

                    {/* Yellow Cards - Only for COMPETITIVE */}
                    {game.game_type === GameType.COMPETITIVE && (
                      <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                        <Label classNames={["l4Regular", "colorPrimaryMain"]}>Yellow Cards</Label>
                        <Div classNames={["flexRow", "alignItemsCenter"]}>
                          <Chip
                            title={"-"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, -1)}
                          ></Chip>
                          <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.yellow_cards || 0}</Label>
                          <Chip
                            title={"+"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, 1)}
                          ></Chip>
                        </Div>
                      </Div>
                    )}

                    {/* Red Cards - Only for COMPETITIVE */}
                    {game.game_type === GameType.COMPETITIVE && (
                      <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                        <Label classNames={["l4Regular", "colorPrimaryMain"]}>Red Cards</Label>
                        <Div classNames={["flexRow", "alignItemsCenter"]}>
                          <Chip
                            title={"-"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, undefined, -1)}
                          ></Chip>
                          <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.red_cards || 0}</Label>
                          <Chip
                            title={"+"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, undefined, 1)}
                          ></Chip>
                        </Div>
                      </Div>
                    )}

                    {/* Saves - Only for COMPETITIVE */}
                    {game.game_type === GameType.COMPETITIVE && (
                      <Div classNames={["flexRow", "justifyContentSpaceBetween", "alignItemsCenter"]}>
                        <Label classNames={["l4Regular", "colorPrimaryMain"]}>Saves</Label>
                        <Div classNames={["flexRow", "alignItemsCenter"]}>
                          <Chip
                            title={"-"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, undefined, undefined, -1)}
                          ></Chip>
                          <Label classNames={["l3Regular", "mh-{8}", "w-{30}", "textCenter"]}>{attendee.saves || 0}</Label>
                          <Chip
                            title={"+"}
                            classNames={["l2Regular"]}
                            onPress={() => updatePlayerStats(attendee, undefined, undefined, undefined, undefined, undefined, undefined, 1)}
                          ></Chip>
                        </Div>
                      </Div>
                    )}
                  </Div>
                )}
              </Div>
            </Div>
          ))
        )}
      </Div>

      {/* Save button */}
      <Button
        title="Save"
        classNames={["btnBlock", "mt-{24}", "mb-{16}"]}
        isLoading={false}
        disabled={!unsavedChanges || unsavedChanges.length === 0}
        type="button"
        onPress={saveChanges}
      />
    </Div>
  );
}
